import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import Title from '../../../components/Title';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, RowId } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { shouldRenderForbiddenAction } from '../../../utils/permission-ui';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
import { ImportLendExecutionLog } from '../../../models/importLendExecutionLog';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('es-CO');
};

const formatDuration = (durationMs: number) => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return '0 s';

  const seconds = Math.round(durationMs / 1000);
  if (seconds < 60) return `${seconds} s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes} min ${remainingSeconds} s`;
};

const getStatusLabel = (status: string, warning: boolean) => {
  if (warning) return 'Con advertencias';
  if (status === 'success') return 'Exitoso';
  if (status === 'error') return 'Error';
  return 'En proceso';
};

const statusColorMap: Record<string, string> = {
  success: '#1b5e20',
  warning: '#b26a00',
  error: '#b71c1c',
  running: '#0d47a1',
};

const ImportLendLogs: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  document.title = 'Historial de importaciones';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { drawerOpen } = useDrawer();
  const { canRead, canDelete, canDeleteSelected } = useResourcePermissions('/procesos/importaciones');
  const showDeleteActions = shouldRenderForbiddenAction(canDelete);
  const showDeleteSelectedActions = shouldRenderForbiddenAction(canDeleteSelected);

  const [logs, setLogs] = useState<ImportLendExecutionLog[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [collects, setCollects] = useState<Collect[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [collectId, setCollectId] = useState('');
  const [selectedIds, setSelectedIds] = useState<RowId[]>([]);

  const loadLogs = async (officeFilter = officeId, collectFilter = collectId) => {
    if (!canRead) {
      setLogs([]);
      return;
    }
    showLoading();
    try {
      const { data } = await axios.get('process/importlend/logs', {
        params: {
          office_id: officeFilter || undefined,
          collect_id: collectFilter || undefined,
          limit: 300,
        },
      });

      setLogs(data?.data ?? []);
    } catch (error: any) {
      console.error('Error cargando historial de importaciones:', error);
      showAlert(error?.response?.data?.message || 'No fue posible cargar el historial de importaciones.', 'error');
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      showLoading();
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        showAlert('No fue posible cargar las oficinas.', 'error');
      } finally {
        hideLoading();
      }
    };

    bootstrap();
    if (canRead) {
      loadLogs('', '');
    }
  }, [canRead]);

  useEffect(() => {
    if (!officeId) {
      setCollects([]);
      setCollectId('');
      return;
    }

    const fetchCollects = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`collectsoffice/${officeId}`);
        setCollects(data ?? []);
      } catch (error) {
        setCollects([]);
        showAlert('No fue posible cargar los cobros de la oficina.', 'warning');
      } finally {
        hideLoading();
      }
    };

    fetchCollects();
  }, [officeId]);

  const handleSearch = async () => {
    if (!canRead) {
      showAlert('Sin permiso para consultar importaciones.', 'warning');
      return;
    }
    await loadLogs();
  };

  const handleDeleteLog = async (id: number) => {
    if (!canDelete) return;
    if (!window.confirm('¿Estás seguro de eliminar este log de importación?')) return;

    showLoading();
    try {
      await axios.delete(`process/importlend/logs/${id}`);
      setLogs((prev) => prev.filter((item) => item.id !== id));
      setSelectedIds((prev) => prev.filter((item) => item !== id));
      showAlert('Log eliminado correctamente.', 'success');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || 'No fue posible eliminar el log.', 'error');
    } finally {
      hideLoading();
    }
  };

  const handleDeleteSelected = async () => {
    if (!canDeleteSelected) return;
    if (selectedIds.length === 0) {
      showAlert('Debes seleccionar al menos un log.', 'warning');
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar ${selectedIds.length} log(s)?`)) return;

    showLoading();
    try {
      await axios.post('process/importlend/logs/delete', {
        ids: selectedIds.map((id) => Number(id)),
      });

      const idsToDelete = new Set(selectedIds.map((id) => Number(id)));
      setLogs((prev) => prev.filter((item) => !idsToDelete.has(item.id)));
      setSelectedIds([]);
      showAlert('Logs eliminados correctamente.', 'success');
    } catch (error: any) {
      showAlert(error?.response?.data?.message || 'No fue posible eliminar los logs.', 'error');
    } finally {
      hideLoading();
    }
  };

  const columns: SimpleColumn[] = [
    { field: 'trace_id', headerName: 'Trace ID', minWidth: 180 },
    {
      field: 'status_label',
      headerName: 'Estado',
      minWidth: 150,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: statusColorMap[row.status_key] || statusColorMap.running,
          }}
        >
          {row.status_label}
        </Typography>
      ),
    },
    { field: 'office_name', headerName: 'Oficina', minWidth: 180, wrapText: true },
    { field: 'collect_name', headerName: 'Cobro', minWidth: 180, wrapText: true },
    { field: 'user_name', headerName: 'Usuario', minWidth: 180, wrapText: true },
    { field: 'started_at_label', headerName: 'Inicio', minWidth: 180 },
    { field: 'finished_at_label', headerName: 'Fin', minWidth: 180 },
    { field: 'duration_label', headerName: 'Duración', minWidth: 120 },
    { field: 'rows_received', headerName: 'Filas', minWidth: 90, align: 'center' },
    { field: 'prepared_rows', headerName: 'Preparadas', minWidth: 110, align: 'center' },
    { field: 'saved_rows', headerName: 'Guardadas', minWidth: 110, align: 'center' },
    { field: 'validation_error_count', headerName: 'Err. validación', minWidth: 130, align: 'center' },
    { field: 'post_process_error_count', headerName: 'Err. procesos', minWidth: 120, align: 'center' },
    { field: 'message', headerName: 'Mensaje', minWidth: 360, wrapText: true },
    {
      field: 'opt_delete',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }) => (
        <PermissionButton
          allowed={canDelete}
          deniedTooltip="Sin permiso para eliminar"
          tooltip="Eliminar"
          variant="contained"
          color="error"
          sx={simpleTableIconButtonSx}
          onClick={() => handleDeleteLog(Number(row.id))}
        >
          <DeleteIcon fontSize="small" />
        </PermissionButton>
      ),
    },
  ];

  const rows = logs.map((log) => ({
    id: log.id,
    trace_id: log.trace_id,
    status_key: log.warning ? 'warning' : log.status,
    status_label: getStatusLabel(log.status, Boolean(log.warning)),
    office_name: log.office?.name ?? '',
    collect_name: log.collect?.name ?? '',
    user_name: log.user?.name ?? '',
    started_at_label: formatDateTime(log.started_at),
    finished_at_label: formatDateTime(log.finished_at),
    duration_label: formatDuration(Number(log.duration_ms ?? 0)),
    rows_received: Number(log.rows_received ?? 0),
    prepared_rows: Number(log.prepared_rows ?? 0),
    saved_rows: Number(log.saved_rows ?? 0),
    validation_error_count: Number(log.validation_error_count ?? 0),
    post_process_error_count: Number(log.post_process_error_count ?? 0),
    message: log.error_message || log.message || '',
  }));

  return (
    <HeaderResponsive>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
        mb={2}
      >
        <Title>Historial de importaciones</Title>
        <Box display="flex" gap={1} ml="auto" flexWrap="wrap">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} component={NavLink} to="/procesos/importarprestamo">
            Regresar
          </Button>
          <Button variant="contained" color="primary" startIcon={<UploadFileIcon />} component={NavLink} to="/procesos/importarprestamo">
            Ir a importar
          </Button>
          {showDeleteSelectedActions && (
            <Tooltip title={canDeleteSelected ? 'Eliminar seleccionados' : 'Sin permiso para eliminar seleccionados'}>
              <span>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleDeleteSelected}
                  disabled={!canDeleteSelected || selectedIds.length === 0}
                >
                  Eliminar seleccionados
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Oficina</InputLabel>
                <Select
                  value={officeId}
                  label="Oficina"
                  onChange={(event) => setOfficeId(String(event.target.value))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id}>{office.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!officeId}>
                <InputLabel>Cobro</InputLabel>
                <Select
                  value={collectId}
                  label="Cobro"
                  onChange={(event) => setCollectId(String(event.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {collects.map((collect) => (
                    <MenuItem key={collect.id} value={collect.id}>{collect.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <PermissionButton
                allowed={canRead}
                deniedTooltip="Sin permiso para consultar"
                tooltip="Consultar"
                fullWidth
                variant="contained"
                startIcon={<HistoryIcon />}
                onClick={handleSearch}
                sx={{ height: '40px' }}
              >
                Consultar
              </PermissionButton>
            </Grid>
          </Grid>

          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={columns}
              checkboxSelection
              selectionModel={selectedIds}
              onRowSelectionModelChange={setSelectedIds}
              maxHeight={560}
              exportCsv
              exportFileName="historial-importaciones.csv"
              columnGroups={[{ header: 'Opciones', fields: ['opt_delete'] }]}
              wrapCellContent
            />
          ) : (
            <EmptyStateMessage message="No existen logs de importación para los filtros seleccionados." align="left" />
          )}
        </CardContent>
      </Card>
    </HeaderResponsive>
  );
};

export default ImportLendLogs;
